export type AllocateOptions = {
  /**
   * Rotates the tie-break order so the spare minor units do not always land on the same
   * participant. Use a stable id (e.g. the transaction id). Without a seed, ties go to the
   * earliest participant in the given order.
   */
  readonly tieBreakSeed?: string;
};

/** 32-bit FNV-1a hash — small, stable across platforms, good enough to pick a rotation. */
export function fnv1a32(text: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

/**
 * Splits `total` minor units proportionally to `weights` using the largest-remainder method.
 *
 * Guarantees: the parts always sum to `total`; zero weights get zero; the result depends only
 * on the inputs (deterministic). Each part differs from its exact share by less than one unit.
 * Negative totals are allocated as their absolute value and negated, so a refund reverses
 * the original split unit for unit.
 */
export function allocate(
  total: bigint,
  weights: readonly bigint[],
  options: AllocateOptions = {},
): bigint[] {
  const count = weights.length;
  if (count === 0) throw new RangeError('Cannot allocate between zero participants');
  let weightSum = 0n;
  for (const weight of weights) {
    if (weight < 0n) throw new RangeError('Allocation weights must not be negative');
    weightSum += weight;
  }
  if (weightSum === 0n) throw new RangeError('Allocation weights must not all be zero');

  if (total < 0n) return allocate(-total, weights, options).map((part) => -part);

  const parts: bigint[] = new Array<bigint>(count);
  const remainders: bigint[] = new Array<bigint>(count);
  let allocated = 0n;
  for (let i = 0; i < count; i++) {
    const exact = total * (weights[i] as bigint);
    parts[i] = exact / weightSum;
    remainders[i] = exact % weightSum;
    allocated += parts[i] as bigint;
  }

  let spare = total - allocated; // always < count
  if (spare === 0n) return parts;

  const offset = options.tieBreakSeed === undefined ? 0 : fnv1a32(options.tieBreakSeed) % count;
  const position = (index: number): number => (index - offset + count) % count;
  const order = [...Array(count).keys()].sort((a, b) => {
    const ra = remainders[a] as bigint;
    const rb = remainders[b] as bigint;
    if (ra !== rb) return ra > rb ? -1 : 1;
    return position(a) - position(b);
  });

  for (const index of order) {
    if (spare === 0n) break;
    if ((remainders[index] as bigint) === 0n) break; // only participants with a fractional share
    parts[index] = (parts[index] as bigint) + 1n;
    spare -= 1n;
  }
  return parts;
}
